// API configuration and utilities
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3002';
const DEBUG_API = process.env.REACT_APP_DEBUG_API === 'true';
const LOCAL_API_FALLBACK = process.env.REACT_APP_LOCAL_API_URL || 'http://localhost:3002';

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: any;
  };
}

const debugLog = (...args: any[]) => {
  if (DEBUG_API) {
    console.log(...args);
  }
};

class ApiService {
  private baseURL: string;
  private readonly responseCache = new Map<string, { response: ApiResponse<any>; updatedAt: number }>();
  private readonly inFlightGets = new Map<string, Promise<ApiResponse<any>>>();
  private readonly freshForMs = 15_000;
  private readonly keepStaleForMs = 5 * 60_000;

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL;
  }

  private shouldTryLocalFallback(): boolean {
    return /trycloudflare\.com/i.test(this.baseURL);
  }

  private handleUnauthorized(endpoint: string, status: number): void {
    if (status === 401 && !endpoint.includes('/auth/login')) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
      window.dispatchEvent(new Event('auth:logout'));
    }
  }

  private async fetchWithBase(
    baseURL: string,
    endpoint: string,
    options: RequestInit,
    token: string,
  ): Promise<Response> {
    const url = `${baseURL}${endpoint}`;
    const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
    debugLog(`API Request: ${options.method || 'GET'} ${url}`);
    return fetch(url, {
      headers: {
        ...(!isFormData && { 'Content-Type': 'application/json' }),
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    });
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    debugLog('API Request options:', options);

    // Get real JWT from localStorage
    const token = localStorage.getItem('adminToken') || '';

    try {
      let activeBaseURL = this.baseURL;
      let response = await this.fetchWithBase(activeBaseURL, endpoint, options, token);

      if (!response.ok && [502, 503, 504].includes(response.status) && this.shouldTryLocalFallback()) {
        debugLog(`Primary API returned ${response.status}. Retrying with local fallback: ${LOCAL_API_FALLBACK}`);
        activeBaseURL = LOCAL_API_FALLBACK;
        response = await this.fetchWithBase(activeBaseURL, endpoint, options, token);
      }

      debugLog(`API Response status: ${response.status} ${response.statusText}`);

      if (!response.ok) {
        this.handleUnauthorized(endpoint, response.status);
        const errorText = await response.text();
        console.error(`API Error response: ${errorText}`);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Get the response text first to see what we're dealing with
      const responseText = await response.text();
      debugLog('API Raw response (first 200 chars):', responseText.substring(0, 200));

      // Handle empty responses (e.g., 204 No Content from DELETE)
      if (!responseText || responseText.trim() === '') {
        return { success: true, data: null as any };
      }

      try {
        const data = JSON.parse(responseText);
        debugLog('API Response data:', data);
        return { success: true, data };
      } catch (parseError) {
        console.error('JSON Parse Error:', parseError);
        console.error('Response was not valid JSON. Full response:', responseText);
        throw new Error('Invalid JSON response from server');
      }
    } catch (error) {
      if (this.shouldTryLocalFallback()) {
        try {
          debugLog(`API request failed on primary base URL. Retrying with local fallback: ${LOCAL_API_FALLBACK}`);
          const fallbackResponse = await this.fetchWithBase(LOCAL_API_FALLBACK, endpoint, options, token);

          if (fallbackResponse.ok) {
            const responseText = await fallbackResponse.text();
            if (!responseText || responseText.trim() === '') {
              return { success: true, data: null as any };
            }
            const data = JSON.parse(responseText);
            return { success: true, data };
          } else {
            this.handleUnauthorized(endpoint, fallbackResponse.status);
          }
        } catch (fallbackError) {
          debugLog('Fallback API request also failed:', fallbackError);
        }
      }

      console.error('API Request failed:', error);
      console.error('API Error details:', {
        url: `${this.baseURL}${endpoint}`,
        method: options.method || 'GET',
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      return {
        success: false,
        error: {
          code: 'API_ERROR',
          message: error instanceof Error ? error.message : 'Unknown error',
        },
      };
    }
  }

  private getCacheKey(endpoint: string): string {
    const token = localStorage.getItem('adminToken') || 'anonymous';
    return `${this.baseURL}|${token.slice(-16)}|${endpoint}`;
  }

  private fetchAndCache<T>(endpoint: string, cacheKey: string): Promise<ApiResponse<T>> {
    const existing = this.inFlightGets.get(cacheKey) as Promise<ApiResponse<T>> | undefined;
    if (existing) return existing;

    const request = this.request<T>(endpoint, { method: 'GET' })
      .then((response) => {
        if (response.success) {
          this.responseCache.set(cacheKey, { response, updatedAt: Date.now() });
        }
        return response;
      })
      .finally(() => this.inFlightGets.delete(cacheKey));

    this.inFlightGets.set(cacheKey, request);
    return request;
  }

  clearGetCache(): void {
    this.responseCache.clear();
  }

  async get<T>(endpoint: string, forceRefresh = false): Promise<ApiResponse<T>> {
    const cacheKey = this.getCacheKey(endpoint);
    const cached = this.responseCache.get(cacheKey) as { response: ApiResponse<T>; updatedAt: number } | undefined;
    const age = cached ? Date.now() - cached.updatedAt : Number.POSITIVE_INFINITY;

    if (!forceRefresh && cached && age <= this.freshForMs) {
      return cached.response;
    }

    if (!forceRefresh && cached && age <= this.keepStaleForMs) {
      // Return usable data immediately and refresh it once in the background.
      void this.fetchAndCache<T>(endpoint, cacheKey);
      return cached.response;
    }

    return this.fetchAndCache<T>(endpoint, cacheKey);
  }

  async post<T>(endpoint: string, data: any): Promise<ApiResponse<T>> {
    const response = await this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (response.success) this.clearGetCache();
    return response;
  }

  async postForm<T>(endpoint: string, data: FormData): Promise<ApiResponse<T>> {
    const response = await this.request<T>(endpoint, {
      method: 'POST',
      body: data,
    });
    if (response.success) this.clearGetCache();
    return response;
  }

  async put<T>(endpoint: string, data: any): Promise<ApiResponse<T>> {
    const response = await this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    if (response.success) this.clearGetCache();
    return response;
  }

  async patch<T>(endpoint: string, data: any): Promise<ApiResponse<T>> {
    const response = await this.request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    if (response.success) this.clearGetCache();
    return response;
  }

  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    const response = await this.request<T>(endpoint, { method: 'DELETE' });
    if (response.success) this.clearGetCache();
    return response;
  }
}

export const apiService = new ApiService();

// Create axios-like interface for backward compatibility
const api = {
  get: async <T = any>(url: string) => {
    const response = await apiService.get<T>(url);
    return { data: response.data };
  },
  post: async <T = any>(url: string, data?: any) => {
    const response = await apiService.post<T>(url, data);
    return { data: response.data };
  },
  put: async <T = any>(url: string, data?: any) => {
    const response = await apiService.put<T>(url, data);
    return { data: response.data };
  },
  patch: async <T = any>(url: string, data?: any) => {
    const response = await apiService.patch<T>(url, data);
    return { data: response.data };
  },
  delete: async <T = any>(url: string) => {
    const response = await apiService.delete<T>(url);
    return { data: response.data };
  },
};

export default api;
